import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretRight } from "@phosphor-icons/react";
import { ActivityIndicator } from "../presence/ActivityIndicator";
import { cn } from "../../lib/cn";

export interface OutlineNode {
  id: string;
  title: string;
  level: number;
  line: number;
  children: OutlineNode[];
}

interface OutlineTreeProps {
  content: string;
  docType: "latex" | "markdown" | "docx";
  activeLine?: number;
  onNavigate?: (line: number) => void;
  className?: string;
}

function parseLatexOutline(content: string): OutlineNode[] {
  const lines = content.split("\n");
  const nodes: OutlineNode[] = [];
  const levelMap: Record<string, number> = {
    chapter: 0,
    section: 1,
    subsection: 2,
    subsubsection: 3,
  };

  const regex = /\\(chapter|section|subsection|subsubsection)\*?\{([^}]*)\}/;

  let inBody = false;
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trimStart();
    if (trimmed.startsWith("\\begin{document}")) { inBody = true; continue; }
    if (trimmed.startsWith("\\end{document}")) break;
    if (!inBody || trimmed.startsWith("%")) continue;

    const match = regex.exec(lines[i]);
    if (match) {
      nodes.push({
        id: `outline-${i}`,
        title: match[2].trim(),
        level: levelMap[match[1]] ?? 1,
        line: i + 1,
        children: [],
      });
    }
  }

  return buildTree(nodes);
}

function parseMarkdownOutline(content: string): OutlineNode[] {
  const lines = content.split("\n");
  const nodes: OutlineNode[] = [];
  const regex = /^(#{1,6})\s+(.+)/;

  for (let i = 0; i < lines.length; i++) {
    const match = regex.exec(lines[i]);
    if (match) {
      nodes.push({
        id: `outline-${i}`,
        title: match[2].trim(),
        level: match[1].length - 1,
        line: i + 1,
        children: [],
      });
    }
  }

  return buildTree(nodes);
}

function parseDocxOutline(content: string): OutlineNode[] {
  const lines = content.split("\n");
  const nodes: OutlineNode[] = [];
  const regex = /<h([1-6])[^>]*>(.*?)<\/h\1>/i;

  for (let i = 0; i < lines.length; i++) {
    const match = regex.exec(lines[i]);
    if (match) {
      nodes.push({
        id: `outline-${i}`,
        title: match[2].replace(/<[^>]*>/g, "").trim(),
        level: parseInt(match[1]) - 1,
        line: i + 1,
        children: [],
      });
    }
  }

  return buildTree(nodes);
}

function buildTree(flatNodes: OutlineNode[]): OutlineNode[] {
  if (flatNodes.length === 0) return [];

  const root: OutlineNode[] = [];
  const stack: OutlineNode[] = [];

  for (const node of flatNodes) {
    const copy = { ...node, children: [] };

    while (stack.length > 0 && stack[stack.length - 1].level >= copy.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      root.push(copy);
    } else {
      stack[stack.length - 1].children.push(copy);
    }
    stack.push(copy);
  }

  return root;
}

function OutlineItem({
  node,
  activeLine,
  endLine = Infinity,
  minLevel,
  onNavigate,
}: {
  node: OutlineNode;
  activeLine?: number;
  endLine?: number;
  minLevel: number;
  onNavigate?: (line: number) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const hasChildren = node.children.length > 0;
  const sectionEnd = hasChildren ? node.children[0].line : endLine;
  const isActive =
    activeLine !== undefined &&
    activeLine >= node.line &&
    activeLine < sectionEnd;
  const indent = node.level - minLevel;

  return (
    <div>
      <div
        className={cn(
          "relative flex w-full items-center gap-1 px-2 py-1 text-left text-xs",
          "hover:bg-[var(--bg-surface-hover)] transition-colors cursor-pointer",
          isActive
            ? "text-[var(--color-cool-azure)] font-medium bg-[var(--color-cool-azure)]/8"
            : "text-[var(--text-secondary)]",
        )}
        style={{ paddingLeft: `${8 + indent * 12}px` }}
      >
        <ActivityIndicator sectionId={node.id} />
        <button
          className="inline-flex w-[10px] shrink-0 items-center justify-center"
          onClick={(e) => {
            if (hasChildren) {
              e.stopPropagation();
              setCollapsed((c) => !c);
            }
          }}
        >
          {hasChildren && (
            <motion.span
              animate={{ rotate: collapsed ? 0 : 90 }}
              transition={{ duration: 0.15 }}
              className="inline-flex"
            >
              <CaretRight
                size={10}
                weight="bold"
                className="text-[var(--text-muted)]"
              />
            </motion.span>
          )}
        </button>
        <span
          className="truncate flex-1"
          onClick={() => onNavigate?.(node.line)}
        >
          {node.title}
        </span>
        <span className="ml-auto shrink-0 text-[10px] text-[var(--text-muted)] tabular-nums">
          {node.line}
        </span>
      </div>

      <AnimatePresence initial={false}>
        {!collapsed && hasChildren && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            {node.children.map((child, i) => (
              <OutlineItem
                key={child.id}
                node={child}
                activeLine={activeLine}
                endLine={node.children[i + 1]?.line ?? endLine}
                minLevel={minLevel}
                onNavigate={onNavigate}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function getMinLevel(nodes: OutlineNode[]): number {
  let min = Infinity;
  for (const n of nodes) {
    min = Math.min(min, n.level);
    if (n.children.length > 0) min = Math.min(min, getMinLevel(n.children));
  }
  return min;
}

export function OutlineTree({
  content,
  docType,
  activeLine,
  onNavigate,
  className,
}: OutlineTreeProps) {
  const outline = useMemo(() => {
    if (docType === "latex") return parseLatexOutline(content);
    if (docType === "docx") return parseDocxOutline(content);
    return parseMarkdownOutline(content);
  }, [content, docType]);

  const minLevel = useMemo(() => getMinLevel(outline), [outline]);

  if (outline.length === 0) {
    return (
      <div className={cn("text-xs text-[var(--text-muted)] p-2", className)}>
        No headings found. Add sections to see the outline.
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-0.5 p-2", className)}>
      {outline.map((node, i) => (
        <OutlineItem
          key={node.id}
          node={node}
          activeLine={activeLine}
          endLine={outline[i + 1]?.line ?? Infinity}
          minLevel={minLevel}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}
